import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import PartialTable from 'components/partials/PartialTable';
import moment from 'moment';
import useHandleModal from 'hooks/useHandleModal';
import NotesPopup from './NotesPopup';
import DiscrepancyImageGalary from './DiscrepancyImageGalary';
import LiveDiscrepancyVirtualizedTable from './LiveDiscrepancyVirtualizedTable';
import { resolvedDiscrepancy } from 'services/stock-location/discrepancyService';
import UsersInCellWidget from 'components/common/users/UsersInCellWidget';

const DeliveryDiscrepancyTable = ({
  data,
  paging,
  total,
  nextPage,
  previousPage,
  gotoPage,
  mode, // "live" or "historic"
  reloadFn
}) => {
  const {
    isModal: isNoteModal,
    dataModal: noteData,
    openModal: openNoteModal,
    closeModal: closeNoteModal,
    submitModal: submitNoteModal
  } = useHandleModal(() => {});

  const {
    isModal: isImageModal,
    dataModal: imageData,
    openModal: openImageModal,
    closeModal: closeImageModal
  } = useHandleModal(() => {});

  const handleStatusChange = useCallback(
    async (id, status) => {
      if (status == 1) {
        const res = await resolvedDiscrepancy(id);
        if (res?.success == true) {
          reloadFn();
        }
      }
    },
    [reloadFn]
  );

  const handleViewImages = useCallback(
    payload => openImageModal(payload),
    [openImageModal]
  );

  const handleViewNotes = useCallback(
    payload => openNoteModal(payload),
    [openNoteModal]
  );

  const historicColumns = useMemo(() => {
    if (mode !== 'historic') return [];

    return [
      { accessor: 'id', Header: 'DiscrepancyId', isVisible: false },
      {
        accessor: 'dateRaised',
        Header: 'Date raised',
        Cell: rowData => {
          const { dateRaised } = rowData.row.original;
          return (
            <span className="cursor-pointer">
              {moment.utc(dateRaised).local().format('DD/MM/YYYY HH:mm')}
            </span>
          );
        }
      },
      { accessor: 'supplierName', Header: 'Supplier' },
      { accessor: 'wpoRef', Header: 'WPO ref' },
      { accessor: 'grnRef', Header: 'GRN ref' },
      { accessor: 'partCode', Header: 'Part code' },
      { accessor: 'supplierPartCode', Header: 'Supplier part code' },
      { accessor: 'partDescription', Header: 'Part description' },
      { accessor: 'deliveryNoteQty', Header: 'Del note QTY' },
      { accessor: 'receivedQty', Header: 'Received QTY' },
      {
        accessor: 'images',
        Header: 'Images',
        Cell: rowData => {
          const { grnRef, wpoRef } = rowData.row.original;
          return (
            <span
              className="text-primary cursor-pointer"
              onClick={() => handleViewImages({ grn: grnRef, wpoRef })}
            >
              View
            </span>
          );
        }
      },
      {
        accessor: 'notes',
        Header: 'Notes',
        Cell: rowData => {
          const { id, noteCount } = rowData.row.original;
          return (
            <span
              className="text-primary cursor-pointer"
              onClick={() => handleViewNotes({ id })}
            >
              View ({noteCount})
            </span>
          );
        }
      },
      {
        accessor: 'dateResolved',
        Header: 'Date resolved',
        Cell: rowData => {
          const { dateResolved } = rowData.row.original;
          return (
            <span className="cursor-pointer">
              {moment.utc(dateResolved).local().format('DD/MM/YYYY HH:mm')}
            </span>
          );
        }
      },
      {
        accessor: 'resolvedBy',
        Header: 'Resolved by',
        Cell: rowData => {
          const { resolvedBy } = rowData.row.original;
          return (
            <UsersInCellWidget usersText={resolvedBy?.toString()} showName />
          );
        }
      }
    ];
  }, [mode, handleViewImages, handleViewNotes]);

  const handleSubmitNote = () => {
    reloadFn();
  };

  return (
    <>
      {mode === 'live' ? (
        <LiveDiscrepancyVirtualizedTable
          data={data}
          onViewImages={handleViewImages}
          onViewNotes={handleViewNotes}
          onStatusChange={handleStatusChange}
        />
      ) : (
        <div className="sticky-zone">
          <PartialTable
            option={{ isStickyHeader: true }}
            data={data}
            columns={historicColumns}
            paging={paging}
            total={total}
            nextPage={nextPage}
            previousPage={previousPage}
            gotoPage={gotoPage}
          />
        </div>
      )}

      <NotesPopup
        isShow={isNoteModal}
        handleClose={closeNoteModal}
        data={noteData}
        handleSubmit={handleSubmitNote}
      />

      <DiscrepancyImageGalary
        isShow={isImageModal}
        handleClose={closeImageModal}
        data={imageData}
        handleSubmit={submitNoteModal}
      />
    </>
  );
};

DeliveryDiscrepancyTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  paging: PropTypes.object,
  total: PropTypes.number,
  nextPage: PropTypes.func,
  previousPage: PropTypes.func,
  gotoPage: PropTypes.func,
  reloadFn: PropTypes.func,
  mode: PropTypes.oneOf(['live', 'historic']).isRequired
};

export default React.memo(DeliveryDiscrepancyTable);
